import * as moment from "moment-timezone";
import {
  calcularTiempoTrabajado,
  calcularTiempoTrabajadoMismoDia,
} from "../interfaces/formik/IReporteRelojChecador.ts";
import { ReporteChequeo } from "../models/ReporteChequeo.ts";
import { buildVisitRowsForPartnerBranch } from "./visitPairingReporteChequeo.ts";
import {
  calculateDailyPayment,
  getDailyPaymentConcept,
  formatPayrollHoursDecimal,
  roundHours,
} from "../services/payroll/ui.ts";
import {
  hasAcceptedFreeDayWorkedIncident,
  withFreeDayWorkedPremium,
} from "../services/payroll/payPremiums.ts";
import { mergeDailyPaymentsByDate } from "./payrollVisitHours.ts";
import {
  additionalMultiVisitExtraPay,
  appendMultiVisitExtrasToConcept,
} from "./payrollMultiVisitExtras.ts";
import {
  resolveIncidentRealChecks,
  resolveScheduledTimesForDisplay,
} from "./incidentDisplayTimes.ts";
import { getIncidentRecordBranchId } from "./incidentBranchMeta.ts";
import type { BranchReportMeta } from "./payrollBranchMeta.ts";
import {
  effectiveNightMetaForPartner,
  partnerShiftFromIncidentSchedule,
} from "./nightScheduleEligibility.ts";
import {
  effectivePayrollDayVisits,
  resolveIncidentFinalStatus,
} from "./payrollUnscheduledIncident.ts";

export { resolveIncidentFinalStatus } from "./payrollUnscheduledIncident.ts";

export function isRejectedExtraRelatedIncident(incident: any): boolean {
  if (!incident || resolveIncidentFinalStatus(incident) !== false) return false;
  const s = incident.status;
  return (
    s === "Salió tarde" ||
    s === "Llegó temprano"
  );
}

export function scheduleHasRejectedExtraPayIncident(
  incidents: any[] = []
): boolean {
  return incidents.some(isRejectedExtraRelatedIncident);
}

export function isAcceptedExtraRelatedIncident(incident: any): boolean {
  if (!incident || resolveIncidentFinalStatus(incident) !== true) return false;
  const s = incident.status;
  return s === "Salió tarde" || s === "Llegó temprano";
}

/**
 * Horas de la incidencia (columna Tiempo). No depende de aprobación/rechazo final.
 */
export type ComputeIncidentWorkedHoursOptions = {
  payrollBranchMeta?: Record<number, BranchReportMeta>;
};

export function computeIncidentWorkedHours(
  record: any,
  nightOrMeta: boolean | ComputeIncidentWorkedHoursOptions = false
): number {
  const status = record?.status;
  let nightScheduleEnabled = false;
  let checkOptions: boolean | Parameters<typeof resolveIncidentRealChecks>[1] =
    false;

  if (typeof nightOrMeta === "boolean") {
    nightScheduleEnabled = nightOrMeta;
    checkOptions = nightOrMeta;
  } else if (nightOrMeta?.payrollBranchMeta) {
    const branchId = getIncidentRecordBranchId(record);
    const shiftName = partnerShiftFromIncidentSchedule(record?.schedule);
    const branchMeta = nightOrMeta.payrollBranchMeta[branchId];
    const effective = effectiveNightMetaForPartner(branchMeta, shiftName);
    nightScheduleEnabled = effective.nightScheduleEnabled;
    checkOptions = { branchMeta, shiftName };
  }

  const { checkIn: realIn, checkOut: realOut } = resolveIncidentRealChecks(
    record,
    checkOptions
  );
  const check =
    realIn?.checkTime ??
    record?.schedule?.assistanceCheck?.checkTime ??
    record?.schedule?.assistanceCheck?.check_time;
  const checkout =
    realOut?.checkTime ??
    record?.schedule?.assistanceCheckout?.checkTime ??
    record?.schedule?.assistanceCheckout?.check_time;

  const roundWorked = (hours: number) => Math.round(hours * 100) / 100;

  /** No programado: siempre horas reales trabajadas (aceptado o rechazado). */
  if (status === "No programado") {
    if (!check || !checkout) {
      return 0;
    }
    return roundWorked(calcularTiempoTrabajado(check, checkout));
  }

  if (!record?.schedule?.startTime || !record?.schedule?.endTime) {
    return 0;
  }

  const scheduled = resolveScheduledTimesForDisplay(
    record.schedule.startTime,
    record.schedule.endTime,
    nightScheduleEnabled
  );
  const start = `${scheduled.entrada}:00`;
  const end =
    scheduled.salida === "00:00" ? "24:00:00" : `${scheduled.salida}:00`;

  if (!check || !checkout || !start || !end) {
    return 0;
  }

  let worked = 0;

  if (status === "Aceptar") {
    const totalWorked = calcularTiempoTrabajado(check, checkout);
    const scheduledHours = calcularTiempoTrabajado(start, end);
    // Legacy «Aceptar» en no programado: mostrar jornada trabajada, no solo el excedente.
    if (totalWorked <= scheduledHours + 0.05) {
      worked = totalWorked;
    } else if (totalWorked > scheduledHours) {
      worked = totalWorked - scheduledHours;
    }
  } else if (status === "Salió tarde" && check !== checkout) {
    worked = calcularTiempoTrabajadoMismoDia(end, checkout);
  } else if (status === "Llegó tarde" && check !== checkout) {
    worked = calcularTiempoTrabajadoMismoDia(start, check);
  } else if (status === "Llegó temprano" && check !== checkout) {
    worked = calcularTiempoTrabajadoMismoDia(check, start);
  } else if (status === "Salió antes" && check !== checkout) {
    worked = calcularTiempoTrabajadoMismoDia(checkout, end);
  } else if (
    status === "No tuvo chequeo de salida" &&
    check !== checkout
  ) {
    const totalWorked = calcularTiempoTrabajado(check, checkout);
    const scheduledHours = calcularTiempoTrabajado(start, end);
    if (totalWorked > scheduledHours) {
      worked = totalWorked - scheduledHours;
    }
  }

  return roundWorked(worked);
}

/** Incidencias del día con finalStatus actualizado (p. ej. desde chart / total-incidents). */
export function mergeIncidentsForPaymentDay(
  dayIncidents: any[] = [],
  allIncidents: any[] = [],
  dateYmd: string
): any[] {
  const byId = new Map<string, any>();
  for (const inc of dayIncidents) {
    if (inc?.id != null) byId.set(String(inc.id), inc);
  }
  for (const inc of allIncidents) {
    const d =
      inc.schedule?.assistanceCheck?.checkDate || inc.schedule?.exactDate;
    if (!d) continue;
    if (moment.utc(d).format("YYYY-MM-DD") !== dateYmd) continue;
    if (inc?.id != null) {
      const key = String(inc.id);
      byId.set(key, { ...(byId.get(key) || {}), ...inc });
    }
  }
  if (byId.size > 0) return [...byId.values()];
  return dayIncidents;
}

export function sumRejectedExtraHours(incidents: any[] = []): number {
  let sum = 0;
  for (const inc of incidents) {
    if (!isRejectedExtraRelatedIncident(inc)) continue;
    sum += computeIncidentWorkedHours(inc);
  }
  return Math.round(sum * 100) / 100;
}

export function subtractRejectedExtraHours(
  totalHours: number,
  incidents: any[] = []
): number {
  const rejected = sumRejectedExtraHours(incidents);
  if (rejected <= 0) return totalHours;
  return Math.max(0, Math.round((totalHours - rejected) * 100) / 100);
}

export type ComputeTotalPayWithIncentivesInput = {
  paymentDetail: {
    dailyPayments?: any[];
    freeDaysEnjoyedDates?: string[];
  };
  chartFreeDaysDates?: string[];
  savedIncidents?: any[];
  record: {
    dailySalary: string;
    payPerHour: string;
    NormalCalculationNomina?: boolean;
    totalIncentives?: string;
    totalDeductions?: string;
    tarjetasDeductions?: string | number | null;
    totalDeductionsExcludingTarjetas?: string | number | null;
    uniformDeduction?: string;
    calculatedMissingBoxes?: Array<{ calculatedAmount?: number }>;
    calculatedNewDeductions?: Array<{
      name?: string;
      calculatedAmount?: number;
      amount?: number;
    }>;
  };
  fullDayHours: number;
};

export type PartnerPayBreakdown = {
  totalDailyPayments: number;
  totalIncentives: number;
  totalDeductions: number;
  totalMissingBoxDeductions: number;
  uniformDeduction: number;
  totalPayWithIncentives: number;
};

/**
 * Desglose de pago por colaborador (horas por visita / multi-sucursal).
 * Usar en Cuadro de Mando, Pago Estimado Total y filas del cuadro.
 */
export function computePartnerPayBreakdownFromDetail(
  input: ComputeTotalPayWithIncentivesInput
): PartnerPayBreakdown | null {
  const {
    paymentDetail,
    chartFreeDaysDates = [],
    savedIncidents = [],
    record,
    fullDayHours,
  } = input;

  const dailyPaymentsRaw = paymentDetail.dailyPayments || [];
  if (!dailyPaymentsRaw.length) return null;

  const parseMoney = (v: unknown) =>
    parseFloat(String(v ?? "").replace(/[^0-9.-]+/g, "")) || 0;

  const dailySalary = parseMoney(record.dailySalary);
  const hourlyPay = parseMoney(record.payPerHour);
  const NormalCalculationNomina = record.NormalCalculationNomina || false;

  const freeDaysDatesSet = new Set(
    chartFreeDaysDates.map((date: string) => {
      let normalized = moment(date, "DD-MM-YYYY", true);
      if (!normalized.isValid()) normalized = moment(date, "YYYY-MM-DD", true);
      if (!normalized.isValid()) normalized = moment(date);
      return normalized.isValid() ? normalized.format("YYYY-MM-DD") : date;
    })
  );

  const freeDaysEnjoyedDatesSet = new Set(
    (paymentDetail.freeDaysEnjoyedDates || []).map((date: string) => {
      let normalized = moment(date, "YYYY-MM-DD", true);
      if (!normalized.isValid()) normalized = moment(date, "DD-MM-YYYY", true);
      if (!normalized.isValid()) normalized = moment(date);
      return normalized.isValid() ? normalized.format("YYYY-MM-DD") : date;
    })
  );

  const dailyPayments = dailyPaymentsRaw.map((dp: any) => {
    const hoursInSchedule = dp.hoursInSchedule || 0;
    const totalHoursRaw = Number(dp.hours) || 0;
    const dayDateNormalized = moment(dp.date, ["YYYY-MM-DD", "DD-MM-YYYY"]).isValid()
      ? moment(dp.date, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("YYYY-MM-DD")
      : moment(dp.date).format("YYYY-MM-DD");

    const scheduleIncidents = mergeIncidentsForPaymentDay(
      dp.incidents || [],
      savedIncidents,
      dayDateNormalized
    );
    const { visits: payableVisits, totalHours: payableHoursRaw } =
      effectivePayrollDayVisits(dp, scheduleIncidents);
    const totalHours = subtractRejectedExtraHours(
      payableHoursRaw,
      scheduleIncidents
    );

    const isCelebrityDayWorked = dp.isCelebrityDayWorked || false;
    const celebrityDay = dp.celebrityDay || null;

    const isFreeDayWorked =
      dp.isFreeDayWorked !== undefined
        ? dp.isFreeDayWorked
        : freeDaysDatesSet.has(dayDateNormalized) &&
          payableHoursRaw > 0 &&
          !freeDaysEnjoyedDatesSet.has(dayDateNormalized);

    const scheduleForPayment = buildScheduleForPaymentDay(
      {
        ...dp,
        checkIn: payableVisits[0]?.entradaRegistrada ?? dp.checkIn,
        checkOut:
          payableVisits[payableVisits.length - 1]?.salidaRegistrada ?? dp.checkOut,
        visits: payableVisits,
        visitCount: payableVisits.length || Number(dp.visitCount) || 0,
      },
      scheduleIncidents,
      totalHours,
      { isCelebrityDayWorked, celebrityDay }
    );

    if (dp.hasPaymentOverride === true) {
      return { payment: Number(dp.payment) || 0 };
    }

    const hasRejectedIncidents =
      scheduleHasRejectedExtraPayIncident(scheduleIncidents);
    const hasAcceptedExtraIncident = scheduleIncidents.some(
      isAcceptedExtraRelatedIncident
    );
    const hoursUsedForCalc = NormalCalculationNomina
      ? totalHours
      : hoursInSchedule > 0
        ? hoursInSchedule
        : totalHours;

    let recalculatedPayment = Number(dp.payment) || 0;
    if (hasRejectedIncidents) {
      if (isDoubleShiftRegisteredHours(totalHours, fullDayHours)) {
        recalculatedPayment = dailySalary * 2;
      } else {
        const normalHours = Math.min(hoursUsedForCalc, fullDayHours);
        recalculatedPayment =
          normalHours === fullDayHours
            ? dailySalary
            : Math.floor(normalHours) * hourlyPay;
      }
    } else {
      recalculatedPayment = calculateDailyPayment(
        scheduleForPayment,
        dailySalary,
        hourlyPay,
        NormalCalculationNomina,
        hoursInSchedule,
        fullDayHours
      );
      const chartExtraFromSchedule =
        hoursInSchedule > 0 ? Math.floor(totalHoursRaw - hoursInSchedule) : 0;
      const shouldCapToFullDayWithoutExtra =
        !hasRejectedIncidents &&
        !hasAcceptedExtraIncident &&
        Number(dp.visitCount) <= 1 &&
        hoursInSchedule > 0 &&
        chartExtraFromSchedule <= 0 &&
        hoursUsedForCalc >= fullDayHours;
      if (shouldCapToFullDayWithoutExtra) {
        recalculatedPayment = dailySalary;
      }
      if (hoursInSchedule > 0 && hasAcceptedExtraIncident) {
        const chartExtra = Math.floor(totalHoursRaw - hoursInSchedule);
        const alreadyIncluded = Math.max(
          0,
          Math.floor(roundHours(hoursUsedForCalc, fullDayHours) - fullDayHours)
        );
        const additionalExtra = Math.max(0, chartExtra - alreadyIncluded);
        if (additionalExtra > 0) {
          recalculatedPayment += additionalExtra * hourlyPay;
        }
      }
    }

    if (
      isFreeDayWorked &&
      !hasRejectedIncidents &&
      !hasAcceptedFreeDayWorkedIncident(scheduleIncidents)
    ) {
      recalculatedPayment = withFreeDayWorkedPremium(recalculatedPayment);
    }

    return { payment: recalculatedPayment };
  });

  const allDailyPayments = [...dailyPayments];
  (paymentDetail.freeDaysEnjoyedDates || []).forEach((freeDayDate: string) => {
    const freeDayFormatted = moment(freeDayDate, "YYYY-MM-DD").format("DD/MM/YYYY");
    const exists = allDailyPayments.some(
      (dp) =>
        moment(dp.date, ["DD/MM/YYYY", "YYYY-MM-DD"]).format("DD/MM/YYYY") ===
        freeDayFormatted
    );
    if (!exists) {
      allDailyPayments.push({ payment: dailySalary });
    }
  });

  const totalDailyPaymentsNumber = allDailyPayments.reduce(
    (sum, day) => sum + (Number(day.payment) || 0),
    0
  );

  const totalIncentivesNumber = parseMoney(record.totalIncentives);
  const deductionSplit = splitPayrollDeductionTotalsForCuadro(record);
  const hasCuadroDeductionSplit =
    parseDeductionAmount(record.tarjetasDeductions) > 0 ||
    parseDeductionAmount(record.totalDeductionsExcludingTarjetas) > 0;

  const totalDeductionsNumber = hasCuadroDeductionSplit
    ? deductionSplit.totalAll
    : parseMoney(record.totalDeductions);
  const totalMissingBoxDeductions = hasCuadroDeductionSplit
    ? 0
    : (record.calculatedMissingBoxes || []).reduce(
        (sum, mb) => sum + (mb.calculatedAmount || 0),
        0
      );
  const uniformDeductionAmount = hasCuadroDeductionSplit
    ? 0
    : parseMoney(record.uniformDeduction);

  const totalPayWithIncentives =
    totalDailyPaymentsNumber +
    totalIncentivesNumber -
    (totalDeductionsNumber + totalMissingBoxDeductions + uniformDeductionAmount);

  return {
    totalDailyPayments: totalDailyPaymentsNumber,
    totalIncentives: totalIncentivesNumber,
    totalDeductions: totalDeductionsNumber,
    totalMissingBoxDeductions,
    uniformDeduction: uniformDeductionAmount,
    totalPayWithIncentives,
  };
}

/**
 * Mismo total que PaymentDetail (desglose diario + incentivos − deducciones).
 * Usar para el cuadro de mando tras refrescar payment-detail (el summary del backend puede diferir).
 */
export function computeTotalPayWithIncentivesFromDetail(
  input: ComputeTotalPayWithIncentivesInput
): number {
  return computePartnerPayBreakdownFromDetail(input)?.totalPayWithIncentives ?? 0;
}

/** Objeto schedule para pago/concepto: usa horas sumadas por visita, no primer–última checada. */
export function buildScheduleForPaymentDay(
  dp: any,
  scheduleIncidents: any[],
  registeredHours: number,
  options?: {
    isCelebrityDayWorked?: boolean;
    celebrityDay?: any;
  }
) {
  return {
    assistanceCheck_checkTime: dp.checkIn,
    assistanceCheckout_checkTime: dp.checkOut,
    schedule_startTime: dp.schedule?.startTime || "",
    schedule_endTime: dp.schedule?.endTime || "",
    incidents: scheduleIncidents,
    isCelebrityDayWorked: options?.isCelebrityDayWorked ?? false,
    celebrityDay: options?.celebrityDay ?? null,
    registeredHoursOverride: registeredHours,
    visitCount: Number(dp.visitCount) || 0,
    visits: dp.visits || [],
  };
}

export type BuildReciboDailyPaymentsInput = {
  backendDailyPayments: any[];
  dailySalary: number;
  hourlyPay: number;
  NormalCalculationNomina: boolean;
  fullDayHours: number;
  chartFreeDaysDates?: string[];
  freeDaysEnjoyedDates?: string[];
  savedIncidents?: any[];
};

/**
 * Misma lógica que PaymentDetail.calculateRealDailyPayments cuando hay dailyPayments del API.
 * Usar en recibos masivos para que coincidan con el detalle de pago individual.
 */
export function buildReciboDailyPaymentsFromPaymentDetail(
  input: BuildReciboDailyPaymentsInput
): any[] {
  const {
    backendDailyPayments,
    dailySalary,
    hourlyPay,
    NormalCalculationNomina,
    fullDayHours,
    chartFreeDaysDates = [],
    freeDaysEnjoyedDates = [],
    savedIncidents = [],
  } = input;

  if (!backendDailyPayments?.length) return [];

  const freeDaysDatesSet = new Set(
    chartFreeDaysDates.map((date: string) => {
      let normalized = moment(date, "DD-MM-YYYY", true);
      if (!normalized.isValid()) normalized = moment(date, "YYYY-MM-DD", true);
      if (!normalized.isValid()) normalized = moment(date);
      return normalized.isValid() ? normalized.format("YYYY-MM-DD") : date;
    })
  );

  const freeDaysEnjoyedDatesSet = new Set(
    freeDaysEnjoyedDates.map((date: string) => {
      let normalized = moment(date, "YYYY-MM-DD", true);
      if (!normalized.isValid()) normalized = moment(date, "DD-MM-YYYY", true);
      if (!normalized.isValid()) normalized = moment(date);
      return normalized.isValid() ? normalized.format("YYYY-MM-DD") : date;
    })
  );

  return mergeDailyPaymentsByDate(backendDailyPayments).map((dp: any) => {
    const dpNormalCalculationNomina = NormalCalculationNomina;

    const dayDateNormalized = moment(dp.date, ["YYYY-MM-DD", "DD-MM-YYYY"]).isValid()
      ? moment(dp.date, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("YYYY-MM-DD")
      : moment(dp.date).format("YYYY-MM-DD");

    const scheduleIncidents = mergeIncidentsForPaymentDay(
      dp.incidents || [],
      savedIncidents,
      dayDateNormalized
    );
    const { visits: payableVisits, totalHours: totalHoursRaw } =
      effectivePayrollDayVisits(dp, scheduleIncidents);
    const hoursInSchedule = dp.hoursInSchedule || 0;
    const totalHours = subtractRejectedExtraHours(
      totalHoursRaw,
      scheduleIncidents
    );
    const isCelebrityDayWorked = dp.isCelebrityDayWorked || false;
    const celebrityDay = dp.celebrityDay || null;

    const isFreeDayWorked =
      dp.isFreeDayWorked !== undefined
        ? dp.isFreeDayWorked
        : freeDaysDatesSet.has(dayDateNormalized) &&
          totalHoursRaw > 0 &&
          !freeDaysEnjoyedDatesSet.has(dayDateNormalized);

    const scheduleForPayment = buildScheduleForPaymentDay(
      {
        ...dp,
        checkIn: payableVisits[0]?.entradaRegistrada ?? dp.checkIn,
        checkOut:
          payableVisits[payableVisits.length - 1]?.salidaRegistrada ?? dp.checkOut,
        visits: payableVisits,
        visitCount: payableVisits.length || Number(dp.visitCount) || 0,
      },
      scheduleIncidents,
      totalHours,
      { isCelebrityDayWorked, celebrityDay }
    );

    if (dp.hasPaymentOverride === true) {
      const paymentOverride = Number(dp.payment);

      let concept = "";
      if (
        typeof dp.conceptOverride === "string" &&
        dp.conceptOverride.trim() !== ""
      ) {
        concept = dp.conceptOverride.trim();
      } else {
        concept = getDailyPaymentConcept(
          scheduleForPayment,
          dpNormalCalculationNomina,
          hoursInSchedule,
          fullDayHours
        );
        if (isFreeDayWorked && !concept.includes("descanso trabajado")) {
          concept = concept + " - día de descanso trabajado";
        }
      }

      return {
        date: moment(dayDateNormalized, "YYYY-MM-DD").format("DD/MM/YYYY"),
        hours: formatPayrollHoursDecimal(totalHours),
        hoursInSchedule:
          hoursInSchedule > 0
            ? formatPayrollHoursDecimal(hoursInSchedule)
            : "0.0",
        hoursForDisplay:
          Number(dp.visitCount) > 1
            ? totalHours
            : dpNormalCalculationNomina
            ? totalHours
            : hoursInSchedule > 0
            ? hoursInSchedule
            : totalHours,
        concept,
        payment: paymentOverride,
        checkIn: dp.checkIn || "N/A",
        checkOut: dp.checkOut || "N/A",
        isFreeDayWorked,
        isFreeDayEnjoyed: false,
        isCelebrityDayWorked,
        celebrityDay,
        schedule: dp.schedule,
        incidents: scheduleIncidents,
        hasPaymentOverride: true,
        visitCount: payableVisits.length || dp.visitCount,
        visits: payableVisits,
      };
    }

    const hasRejectedIncidents =
      scheduleHasRejectedExtraPayIncident(scheduleIncidents);
    const hasAcceptedExtraIncident = scheduleIncidents.some(
      isAcceptedExtraRelatedIncident
    );
    const hoursUsedForCalc = dpNormalCalculationNomina
      ? totalHours
      : hoursInSchedule > 0
        ? hoursInSchedule
        : totalHours;
    const chartExtraFromSchedule =
      hoursInSchedule > 0 ? Math.floor(totalHoursRaw - hoursInSchedule) : 0;
    const shouldCapToFullDayWithoutExtra =
      !hasRejectedIncidents &&
      !hasAcceptedExtraIncident &&
      Number(dp.visitCount) <= 1 &&
      hoursInSchedule > 0 &&
      chartExtraFromSchedule <= 0 &&
      hoursUsedForCalc >= fullDayHours;

    let recalculatedPayment = Number(dp.payment) || 0;
    if (hasRejectedIncidents) {
      if (isDoubleShiftRegisteredHours(totalHours, fullDayHours)) {
        recalculatedPayment = dailySalary * 2;
      } else {
        const normalHours = Math.min(hoursUsedForCalc, fullDayHours);
        recalculatedPayment =
          normalHours === fullDayHours
            ? dailySalary
            : Math.floor(normalHours) * hourlyPay;
      }
    } else {
      recalculatedPayment = calculateDailyPayment(
        scheduleForPayment,
        dailySalary,
        hourlyPay,
        dpNormalCalculationNomina,
        hoursInSchedule,
        fullDayHours
      );

      if (shouldCapToFullDayWithoutExtra) {
        recalculatedPayment = dailySalary;
      }

      if (hoursInSchedule > 0 && hasAcceptedExtraIncident) {
        const chartExtra = Math.floor(totalHoursRaw - hoursInSchedule);
        const alreadyIncluded = Math.max(
          0,
          Math.floor(roundHours(hoursUsedForCalc, fullDayHours) - fullDayHours)
        );
        const additionalExtra = Math.max(0, chartExtra - alreadyIncluded);
        if (additionalExtra > 0) {
          recalculatedPayment += additionalExtra * hourlyPay;
        }
      }

    }

    if (
      isFreeDayWorked &&
      !hasRejectedIncidents &&
      !hasAcceptedFreeDayWorkedIncident(scheduleIncidents)
    ) {
      recalculatedPayment = withFreeDayWorkedPremium(recalculatedPayment);
    }

    let concept = "";
    if (hasRejectedIncidents) {
      if (isDoubleShiftRegisteredHours(totalHours, fullDayHours)) {
        const totalHoursRounded = Math.floor(totalHours);
        concept = `${totalHoursRounded}h (doble jornada) (incidencias rechazadas)`;
      } else {
        const normalHours = Math.min(hoursUsedForCalc, fullDayHours);
        if (normalHours === fullDayHours) {
          concept = `${normalHours}h (jornada completa) (incidencias rechazadas)`;
        } else {
          const displayHours = Math.floor(hoursUsedForCalc);
          concept = `${displayHours}h × pago por hora (incidencias rechazadas)`;
        }
      }
    } else {
      concept = getDailyPaymentConcept(
        scheduleForPayment,
        dpNormalCalculationNomina,
        hoursInSchedule,
        fullDayHours
      );
      if (shouldCapToFullDayWithoutExtra) {
        concept = `${fullDayHours}h (jornada completa)`;
      }
      if (hoursInSchedule > 0 && hasAcceptedExtraIncident) {
        const chartExtra = Math.floor(totalHoursRaw - hoursInSchedule);
        const alreadyIncluded = Math.max(
          0,
          Math.floor(roundHours(hoursUsedForCalc, fullDayHours) - fullDayHours)
        );
        const additionalExtra = Math.max(0, chartExtra - alreadyIncluded);
        if (additionalExtra > 0) {
          concept = concept + ` + ${additionalExtra}h extras (aceptada)`;
        }
      }
      concept = appendMultiVisitExtrasToConcept(
        concept,
        totalHoursRaw,
        fullDayHours,
        scheduleForPayment,
        dpNormalCalculationNomina
      );
      if (isFreeDayWorked && !concept.includes("descanso trabajado")) {
        concept = concept + " - día de descanso trabajado";
      }
    }

    return {
      date: moment(dayDateNormalized, "YYYY-MM-DD").format("DD/MM/YYYY"),
      hours: formatPayrollHoursDecimal(totalHours),
      hoursInSchedule:
        hoursInSchedule > 0
          ? formatPayrollHoursDecimal(hoursInSchedule)
          : "0.0",
      hoursForDisplay:
        payableVisits.length > 1
          ? totalHours
          : dpNormalCalculationNomina
          ? totalHours
          : hoursInSchedule > 0
          ? hoursInSchedule
          : totalHours,
      concept,
      payment: recalculatedPayment,
      checkIn: payableVisits[0]?.entradaRegistrada ?? dp.checkIn ?? "N/A",
      checkOut:
        payableVisits[payableVisits.length - 1]?.salidaRegistrada ??
        dp.checkOut ??
        "N/A",
      isFreeDayWorked,
      isFreeDayEnjoyed: false,
      isCelebrityDayWorked,
      celebrityDay,
      schedule: dp.schedule,
      incidents: scheduleIncidents,
      hasPaymentOverride: false,
      visitCount: payableVisits.length || dp.visitCount,
      visits: payableVisits,
    };
  });
}

/** Filas sintéticas de descanso disfrutado (igual que PaymentDetail allDailyPayments). */
export function appendReciboFreeDaysEnjoyedRows(
  dailyRows: any[],
  freeDaysEnjoyedDates: string[],
  dailySalary: number
): any[] {
  const payments = [...dailyRows];
  (freeDaysEnjoyedDates || []).forEach((freeDayDate: string) => {
    const existsInPayments = payments.some((dp) => {
      const dpDate = moment(dp.date, ["DD/MM/YYYY", "YYYY-MM-DD"]).format(
        "DD/MM/YYYY"
      );
      const freeDayFormatted = moment(freeDayDate, "YYYY-MM-DD").format(
        "DD/MM/YYYY"
      );
      return dpDate === freeDayFormatted;
    });
    if (!existsInPayments) {
      payments.push({
        date: moment(freeDayDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
        hours: "0.00",
        hoursInSchedule: "0.00",
        hoursForDisplay: 0,
        concept: "Día de descanso disfrutado",
        payment: dailySalary,
        checkIn: "N/A",
        checkOut: "N/A",
        isFreeDayWorked: false,
        isFreeDayEnjoyed: true,
        isCelebrityDayWorked: false,
        celebrityDay: null,
        schedule: undefined,
        incidents: [],
        hasPaymentOverride: false,
      });
    }
  });
  return payments.sort((a, b) => {
    const dateA = moment(a.date, ["DD/MM/YYYY", "YYYY-MM-DD"]).toDate();
    const dateB = moment(b.date, ["DD/MM/YYYY", "YYYY-MM-DD"]).toDate();
    return dateA.getTime() - dateB.getTime();
  });
}

/** Misma idea que RowDetailIncident (solo lectura): aún sin autorización del líder o fila sintética pendiente. */
export function incidentNeedsLeaderReview(incident: any): boolean {
  if (!incident) return false;
  if (incident.isPendingLeaderReview === true) return true;
  const id = incident.id;
  if (typeof id === "string" && id.startsWith("pending-review-")) return true;
  if (!incident.authorizeBy) return true;
  return false;
}

/** Clave DD-MM-YYYY alineada con filas de gráfico / tabla de chequeo. */
export function incidentDisplayDateKeyDDMMYYYY(incident: any): string | null {
  const raw =
    incident?.schedule?.assistanceCheck?.checkDate ||
    incident?.schedule?.exactDate;
  if (!raw) return null;
  return moment.utc(raw).format("DD-MM-YYYY");
}

export function buildPendingLeaderReviewDateSet(
  incidents: any[] | null | undefined
): Set<string> {
  const set = new Set<string>();
  for (const inc of incidents || []) {
    if (!incidentNeedsLeaderReview(inc)) continue;
    const key = incidentDisplayDateKeyDDMMYYYY(inc);
    if (key) set.add(key);
  }
  return set;
}

export function extractScheduleIdsFromSchedules(
  schedules: any[] | null | undefined
): number[] {
  const ids = new Set<number>();
  for (const s of schedules || []) {
    const raw =
      typeof s?.id === "number"
        ? s.id
        : typeof s?.schedule_id === "number"
          ? s.schedule_id
          : typeof s?.scheduleId === "number"
            ? s.scheduleId
            : null;
    if (raw != null && !Number.isNaN(Number(raw))) ids.add(Number(raw));
  }
  return [...ids];
}

/** Normaliza la fecha mostrada en el desglose diario (PaymentDetail) a DD-MM-YYYY. */
export function paymentDetailRowDateKeyDDMMYYYY(dateStr: string): string {
  const m = moment(dateStr, ["DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY"], true);
  return m.isValid() ? m.format("DD-MM-YYYY") : dateStr;
}

/**
 * Filas del desglose que en recibos deben ir a "Días de descanso trabajados",
 * no a "Jornadas completas" (aunque tengan ≥ jornada de horas y no digan "jornada completa").
 */
export function isReciboRestDayWorkedRow(day: any): boolean {
  if (day == null || typeof day !== "object") return false;
  if (day.isFreeDayWorked === true) return true;
  const c = String(day.concept ?? "").toLowerCase();
  if (!c) return false;
  return (
    c.includes("descanso trabajado") ||
    c.includes("día libre trabajado") ||
    c.includes("dia libre trabajado") ||
    c.includes("libre trabaj") ||
    (c.includes("30%") && c.includes("libre"))
  );
}

/** Días de descanso disfrutado: no deben mezclarse con jornada completa ni pagos por hora. */
export function isReciboRestDayEnjoyedRow(day: any): boolean {
  if (day == null || typeof day !== "object") return false;
  if (day.isFreeDayEnjoyed === true) return true;
  const c = String(day.concept ?? "").toLowerCase();
  return c.includes("descanso disfrutado");
}

/** Importe numérico de una fila del desglose (recibo / Excel). */
export function parseReciboPaymentNumber(value: any): number {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") {
    return Number(value.replace(/[^0-9.-]+/g, "") || 0) || 0;
  }
  return 0;
}

function paymentRowDateYmd(day: any): string {
  const m = moment(day?.date, ["DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY"], true);
  if (m.isValid()) return m.format("YYYY-MM-DD");
  const m2 = moment(day?.date);
  return m2.isValid() ? m2.format("YYYY-MM-DD") : "";
}

/**
 * Marca isAbsence y agrega filas placeholder de findPaymentDetail.absenceRows
 * cuando aún no hay fila en dailyPayments (misma idea que PaymentDetail allDailyPayments).
 */
export function mergeAbsenceRowsForRecibo(
  dailyPayments: any[],
  absenceRows?: any[] | null
): any[] {
  const payments = [...(dailyPayments || [])];
  const absenceDateSet = new Set(
    (absenceRows || [])
      .map((row: any) => String(row.normalizedDate || "").trim())
      .filter(Boolean)
  );

  const paymentsWithAbsenceFlag = payments.map((payment: any) => {
    const normalizedDate = paymentRowDateYmd(payment);
    return {
      ...payment,
      isAbsence:
        payment.isAbsence === true ||
        (normalizedDate ? absenceDateSet.has(normalizedDate) : false),
    };
  });

  const existingPaymentDates = new Set(
    paymentsWithAbsenceFlag
      .map((p: any) => paymentRowDateYmd(p))
      .filter(Boolean)
  );

  const extraAbsenceRows = (absenceRows || [])
    .filter((row: any) => {
      const nd = String(row.normalizedDate || "").trim();
      return nd && !existingPaymentDates.has(nd);
    })
    .map((row: any) => ({
      date: moment(row.normalizedDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
      normalizedDate: row.normalizedDate,
      hours: "0.00",
      hoursInSchedule: "0.00",
      hoursForDisplay: 0,
      concept: "Falta",
      payment: 0,
      checkIn: "N/A",
      checkOut: "N/A",
      isFreeDayWorked: false,
      isFreeDayEnjoyed: false,
      isAbsence: true,
      hasPaymentOverride: false,
    }));

  return [...paymentsWithAbsenceFlag, ...extraAbsenceRows].sort(
    (a: any, b: any) => {
      const ta = moment(a.date, ["DD/MM/YYYY", "YYYY-MM-DD"]).valueOf();
      const tb = moment(b.date, ["DD/MM/YYYY", "YYYY-MM-DD"]).valueOf();
      return ta - tb;
    }
  );
}

/**
 * Suma horas extras como el recibo / PaymentDetail: lee el concepto de cada día
 * ("jornada completa + Nh extras"), no el total del gráfico con reglas distintas.
 */
export function sumExtraHoursFromDailyPayments(
  dailyPayments: any[],
  fullDayHours: number
): number {
  let total = 0;

  for (const day of dailyPayments || []) {
    const concept = String(day?.concept || "").toLowerCase();
    const hours = parseFloat(String(day?.hours ?? "0"));

    if (isReciboRestDayWorkedRow(day) || isReciboRestDayEnjoyedRow(day)) {
      continue;
    }
    if (
      concept.includes("festivo trabajado") ||
      concept.includes("día festivo")
    ) {
      continue;
    }
    if (concept.includes("doble jornada") || hours >= fullDayHours * 2 - 0.5) {
      continue;
    }
    if (
      concept.includes("pago por hora") ||
      (hours > 0 && hours < fullDayHours && !concept.includes("jornada completa"))
    ) {
      continue;
    }

    if (concept.includes("jornada completa") && concept.includes("extras")) {
      const extrasMatch = concept.match(/(\d+(?:\.\d+)?)\s*h?\s*extras/i);
      const numExtras = extrasMatch
        ? parseFloat(extrasMatch[1])
        : Math.max(0, Math.floor(hours - fullDayHours));
      if (numExtras > 0) {
        total += numExtras;
      }
      continue;
    }

    if (concept.includes("jornada completa")) {
      continue;
    }

    if (hours >= fullDayHours) {
      const extraHrs = Math.floor(hours - fullDayHours);
      if (extraHrs > 0) {
        total += extraHrs;
      }
    }
  }

  return total;
}

/**
 * true si la fila ya se contabiliza en jornadas / pago por hora / extras / turno doble
 * del armado del recibo (misma lógica que exportToExcel / PDF masivos).
 */
export function dailyPaymentAttributedInReciboIncomeBuckets(
  day: any,
  fullDayHours: number
): boolean {
  const concept = String(day?.concept || "").toLowerCase();
  const hours = parseFloat(String(day?.hours || "0"));

  if (isReciboRestDayWorkedRow(day) || isReciboRestDayEnjoyedRow(day)) {
    return true;
  }
  if (
    concept.includes("festivo trabajado") ||
    concept.includes("día festivo")
  ) {
    return true;
  }
  if (concept.includes("doble jornada") || hours >= 16.5) {
    return true;
  }
  if (
    concept.includes("pago por hora") ||
    (hours > 0 && hours < fullDayHours && !concept.includes("jornada completa"))
  ) {
    return true;
  }
  if (concept.includes("jornada completa") && concept.includes("extras")) {
    return true;
  }
  if (concept.includes("jornada completa")) {
    return true;
  }
  if (hours >= fullDayHours) {
    return true;
  }
  return false;
}

/**
 * Días y horas nominales alineados con nómina / cuadro de mando: filas del desglose con
 * horas >= jornada completa (incluye descanso trabajado y turno doble a nivel de "día completo").
 * El importe del renglón "Jornadas completas" se sigue calculando solo con lo que va a salario diario puro.
 */
export function getReciboJornadasCompletasCuadroDisplay(
  dailyPayments: any[],
  fullDayHours: number
): { dias: number; horasNominales: number } {
  const fullDayRows = (dailyPayments || []).filter((day: any) => {
    const hours = parseFloat(String(day?.hours || "0"));
    return hours >= fullDayHours;
  });
  const dias = fullDayRows.length;
  const horasNominales = fullDayRows.reduce((sum, day: any) => {
    const h = parseFloat(String(day?.hours || "0"));
    return sum + Math.min(h, fullDayHours);
  }, 0);
  return { dias, horasNominales };
}

/**
 * Una sola fila bajo "Otros": "Faltas" con el importe total.
 * Solo si existe al menos un día de falta con pago > 0 (no lista faltas en $0).
 */
export function buildAbsenceOtrosIngresoRows(
  mergedDailyPayments: any[],
  fullDayHours: number,
  formatCurrency: (n: number) => string
): { rows: (string | number)[][]; totalAmount: number } {
  const absenceDays = (mergedDailyPayments || []).filter((d: any) => {
    if (d.isAbsence !== true) return false;
    return !dailyPaymentAttributedInReciboIncomeBuckets(d, fullDayHours);
  });

  const paidAbsenceDays = absenceDays.filter(
    (d: any) => parseReciboPaymentNumber(d.payment) > 0
  );

  if (paidAbsenceDays.length === 0) {
    return { rows: [], totalAmount: 0 };
  }

  let totalAmount = 0;
  let totalHours = 0;
  paidAbsenceDays.forEach((day: any) => {
    totalAmount += parseReciboPaymentNumber(day.payment);
    totalHours += parseFloat(String(day.hours || "0")) || 0;
  });

  const horasStr = totalHours > 0 ? totalHours.toFixed(1) : "";

  const rows: (string | number)[][] = [
    [
      "Faltas",
      paidAbsenceDays.length,
      horasStr,
      formatCurrency(totalAmount),
    ],
  ];

  return { rows, totalAmount };
}

/** Alinea filas de findChartDaysWorked: horasExtras = 0 si hay incidencia rechazada ese día. */
export function applyRejectedIncidentsZeroHorasExtras(
  detailedRows: any[],
  incidents: any[]
): any[] {
  if (!detailedRows?.length) return detailedRows || [];
  return detailedRows.map((record: any) => {
    const dateKey = moment(record.fecha, ["DD-MM-YYYY", "YYYY-MM-DD"], true);
    if (!dateKey.isValid()) {
      return record;
    }
    const ymd = dateKey.format("YYYY-MM-DD");
    const hasRejected = incidents.some((inc: any) => {
      const d =
        inc.schedule?.exactDate ||
        inc.schedule?.assistanceCheck?.checkDate ||
        inc.checkIn?.checkDate;
      if (!d) return false;
      return (
        moment.utc(d).format("YYYY-MM-DD") === ymd &&
        isRejectedExtraRelatedIncident(inc)
      );
    });
    return hasRejected ? { ...record, horasExtras: 0 } : record;
  });
}

/**
 * Umbral alineado con PaymentDetail / recibo: turno doble cuando las horas
 * registradas del día son >= dos jornadas completas menos 0.5 h.
 */
export function isDoubleShiftRegisteredHours(
  horasRegistradas: number,
  fullDayHours: number
): boolean {
  const h = Number(horasRegistradas);
  if (Number.isNaN(h) || !(fullDayHours > 0)) return false;
  return h >= fullDayHours * 2 - 0.5;
}

/**
 * Horas extras para cuadro / gráfico: no sumar la porción que corresponde a jornada doble
 * (evita inflar "extras" con ~8h del segundo turno).
 */
export function chartHorasExtrasExcludingDoubleShift(
  horasRegistradas: number,
  horasExtrasRaw: number,
  fullDayHours: number
): number {
  if (isDoubleShiftRegisteredHours(horasRegistradas, fullDayHours)) return 0;
  const e = Number(horasExtrasRaw);
  return Number.isNaN(e) || e <= 0 ? 0 : e;
}

export interface DetailedCheckRecord {
  branch_id: number;
  checkIn: any;
  checkOut: any;
  date: string;
  hoursWorked: number;
}

export interface PartnerDailyHours {
  partnerId: number;
  date: string;
  checkIn: string;
  checkOut: string;
  hoursWorked: number;
  branchId: number;
  schedule_exactDate?: string;
  isCelebrityDayWorked?: boolean;
  celebrityDay?: any;
}

/**
 * Procesa los registros de checadas del backend que ya vienen con la lógica correcta
 * El backend ya usa la misma lógica que Chequeo.tsx
 */
export function processBackendChecks(payrollData: any[]): PartnerDailyHours[] {
  const data: PartnerDailyHours[] = [];

  for (const record of payrollData) {
    if (record.assistanceCheck_id && record.assistanceCheckout_id) {
      // Calcular horas trabajadas usando entrada y salida del backend
      const hoursWorked = calcularTiempoTrabajado(
        record.assistanceCheck_checkTime,
        record.assistanceCheckout_checkTime
      );

      data.push({
        partnerId: record.partner_id,
        date: new Date(record.schedule_exactDate).toISOString().split('T')[0],
        checkIn: record.assistanceCheck_checkTime,
        checkOut: record.assistanceCheckout_checkTime,
        hoursWorked: hoursWorked,
        branchId: record.branch_id
      });
    }
  }

  return data;
}

/**
 * Procesa las múltiples checadas por día usando formattedChecks del backend
 * Exactamente como lo hace Chequeo.tsx
 */
export function processFormattedChecks(payrollData: any[]): PartnerDailyHours[] {
  const data: PartnerDailyHours[] = [];

  for (const record of payrollData) {
    if (record.formattedChecks && record.formattedChecks.length > 0) {
      // Ordenar checadas por hora
      const sortedChecks = record.formattedChecks.sort((a: any, b: any) =>
        a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
      );

      // Check-in: primera checada después de las 3:00 AM
      const checkIn = sortedChecks.find((check: any) => {
        const [hours, minutes, seconds] = check.assistanceCheck_checkTime
          .split(':')
          .map(Number);
        const totalMinutes = hours * 60 + minutes + seconds / 60;
        return totalMinutes >= 3 * 60; // Después de las 3:00 AM
      });

      // Check-out: última checada hasta las 3:00 AM del día siguiente
      const checkOut = sortedChecks.find((check: any) => {
        const [hours, minutes, seconds] = check.assistanceCheck_checkTime
          .split(':')
          .map(Number);
        const totalMinutes = hours * 60 + minutes + seconds / 60;
        return totalMinutes <= 3 * 60; // Hasta las 3:00 AM
      }) || sortedChecks[sortedChecks.length - 1];

      if (checkIn && checkOut && checkIn.assistanceCheck_id !== checkOut.assistanceCheck_id) {
        // Calcular horas trabajadas
        const hoursWorked = calcularTiempoTrabajado(
          checkIn.assistanceCheck_checkTime,
          checkOut.assistanceCheck_checkTime
        );

        data.push({
          partnerId: record.partner_id,
          date: new Date(record.schedule_exactDate).toISOString().split('T')[0],
          checkIn: checkIn.assistanceCheck_checkTime,
          checkOut: checkOut.assistanceCheck_checkTime,
          hoursWorked: hoursWorked,
          branchId: record.branch_id,
          schedule_exactDate: record.schedule_exactDate,
          isCelebrityDayWorked: record.isCelebrityDayWorked,
          celebrityDay: record.celebrityDay
        });
      }
    }
  }

  return data;
}

/**
 * Procesa los registros de checadas para obtener entrada y salida por día
 * Usa la misma lógica que Chequeo.tsx (para casos donde no hay datos del backend)
 */
export function processDetailedChecks(records: any[], start: string, end: string): PartnerDailyHours[] {
  const days = [...new Set(records.map((record) => record.assistanceCheck_checkDate))];
  const data: PartnerDailyHours[] = [];

  const startDate = new Date(start);
  const endDate = new Date(end);

  for (const day of days) {
    const currentDay = new Date(day);
    const nextDay = new Date(currentDay);
    nextDay.setDate(currentDay.getDate() + 1);

    // Comparar si está dentro del rango
    if (currentDay >= startDate && currentDay <= endDate) {
      const checksCurrent = records.filter(
        (record) => record.assistanceCheck_checkDate === currentDay.toISOString()
      );
      const checksNext = records.filter(
        (record) => record.assistanceCheck_checkDate === nextDay.toISOString()
      );
      
      const partners = [...new Set(checksCurrent.map((record) => record.partner_id))];

      for (const partner of partners) {
        // Obtener las sucursales donde han checado
        const branchesCurrent = checksCurrent
          .filter((record) => record.partner_id === partner)
          .map((record) => record.branch_id);

        const branchesNext = checksNext
          .filter((record) => record.partner_id === partner)
          .map((record) => record.branch_id);

        // Combinar ambas listas y eliminar duplicados
        const branches = [...new Set([...branchesCurrent, ...branchesNext])];

        // Iterar sobre cada sucursal
        for (const branch of branches) {
          const checkCurrent = checksCurrent
            .filter(
              (record) =>
                record.partner_id === partner && record.branch_id === branch
            )
            .sort((a, b) =>
              a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
            );

          const checkNext = checksNext
            .filter(
              (record) =>
                record.partner_id === partner && record.branch_id === branch
            )
            .sort((a, b) =>
              a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
            );

          const visitRows = buildVisitRowsForPartnerBranch(
            checkCurrent as ReporteChequeo[],
            checkNext as ReporteChequeo[],
            branch,
            partner,
            checksCurrent as ReporteChequeo[],
            checksNext as ReporteChequeo[],
            3 * 60,
            false
          );
          for (const row of visitRows) {
            if (!row.checkIn) {
              continue;
            }
            const hoursWorked = calcularTiempoTrabajado(
              row.checkIn.assistanceCheck_checkTime,
              row.checkOut?.assistanceCheck_checkTime
            );
            data.push({
              partnerId: partner,
              date: currentDay.toISOString().split("T")[0],
              checkIn: row.checkIn.assistanceCheck_checkTime,
              checkOut: row.checkOut?.assistanceCheck_checkTime || "",
              hoursWorked,
              branchId: branch,
            });
          }
        }
      }
    }
  }

  return data;
}

/**
 * Obtiene las horas trabajadas por partner y fecha
 */
export function getPartnerHoursByDate(detailedRecords: PartnerDailyHours[], partnerId: number): PartnerDailyHours[] {
  return detailedRecords.filter(record => record.partnerId === partnerId);
}

/**
 * Calcula el total de horas trabajadas por partner
 */
export function calculateTotalHoursForPartner(detailedRecords: PartnerDailyHours[], partnerId: number): number {
  const partnerRecords = getPartnerHoursByDate(detailedRecords, partnerId);
  return partnerRecords.reduce((total, record) => total + record.hoursWorked, 0);
}

export interface ChartDaysSummary {
  totalDays: number;
  completeDays: number;
  hourlyDays: number;
  doubleShiftDays: number;
  extraDays: number;
  zeroDays: number;
  totalHours: number;
  hoursInSchedule: number;
  extraHours: number;
}

export function summarizeChartDays(detailed: any[] = [], fullDayHours: number = 8.5): ChartDaysSummary {
  return detailed.reduce<ChartDaysSummary>(
    (acc, day) => {
      const isFreeDayEnjoyed = day?.isFreeDayEnjoyed === true;
      const horasRegistradas = Number(day?.horasRegistradas ?? 0);
      const horasHorario = Number(day?.horasTrabajadasEnHorario ?? 0);
      const horasExtrasRaw = Number(day?.horasExtras ?? 0);

      acc.totalDays += 1;
      acc.totalHours += horasRegistradas;
      acc.hoursInSchedule += horasHorario;
      acc.extraHours += horasExtrasRaw > 0 ? horasExtrasRaw : 0;

      if (isFreeDayEnjoyed) {
        acc.zeroDays += 1;
        return acc;
      }

      if (horasRegistradas === 0) {
        acc.zeroDays += 1;
      } else if (horasRegistradas < fullDayHours) {
        acc.hourlyDays += 1;
      } else {
        acc.completeDays += 1;
        if (horasRegistradas >= fullDayHours * 2) {
          acc.doubleShiftDays += 1;
        }
      }

      if (horasExtrasRaw > 0) {
        acc.extraDays += 1;
      }

      return acc;
    },
    {
      totalDays: 0,
      completeDays: 0,
      hourlyDays: 0,
      doubleShiftDays: 0,
      extraDays: 0,
      zeroDays: 0,
      totalHours: 0,
      hoursInSchedule: 0,
      extraHours: 0,
    }
  );
}

/** Clave estable para agrupar deducciones: trim, minúsculas, sin acentos, espacios colapsados. */
export function normalizeDeductionGroupKey(name: string): string {
  return (name || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Deducción del catálogo llamada Tarjetas (sin importar mayúsculas/acentos). */
export function isTarjetasDeductionName(name: string): boolean {
  return normalizeDeductionGroupKey(name) === "tarjetas";
}

export function sumTarjetasFromCalculatedDeductions(
  deductions?: Array<{ name?: string; calculatedAmount?: number; amount?: number }> | null
): number {
  let sum = 0;
  for (const d of deductions || []) {
    const amt = parseDeductionAmount(d.calculatedAmount ?? d.amount);
    if (amt <= 0 || !isTarjetasDeductionName(String(d.name ?? ""))) {
      continue;
    }
    sum += amt;
  }
  return sum;
}

export function splitPayrollDeductionTotalsForCuadro(record: {
  totalDeductions?: string | number | null;
  tarjetasDeductions?: string | number | null;
  totalDeductionsExcludingTarjetas?: string | number | null;
  calculatedNewDeductions?: Array<{
    name?: string;
    calculatedAmount?: number;
    amount?: number;
  }> | null;
}): { tarjetas: number; totalDeductionsWithoutTarjetas: number; totalAll: number } {
  const tarjetasFromField = parseDeductionAmount(record.tarjetasDeductions);
  const tarjetas =
    tarjetasFromField > 0
      ? tarjetasFromField
      : sumTarjetasFromCalculatedDeductions(record.calculatedNewDeductions);

  const totalFromRecord = parseDeductionAmount(record.totalDeductions);
  const hasExcludingField =
    record.totalDeductionsExcludingTarjetas != null &&
    String(record.totalDeductionsExcludingTarjetas).trim() !== "";
  let totalDeductionsWithoutTarjetas = hasExcludingField
    ? parseDeductionAmount(record.totalDeductionsExcludingTarjetas)
    : totalFromRecord;

  if (tarjetas > 0) {
    if (totalFromRecord >= tarjetas) {
      const withoutFromFull = Math.max(0, totalFromRecord - tarjetas);
      if (
        !hasExcludingField ||
        Math.abs(totalDeductionsWithoutTarjetas - totalFromRecord) < 0.01
      ) {
        totalDeductionsWithoutTarjetas = withoutFromFull;
      }
    } else {
      totalDeductionsWithoutTarjetas = totalFromRecord;
    }
  }

  const totalAll = totalDeductionsWithoutTarjetas + tarjetas;
  return { tarjetas, totalDeductionsWithoutTarjetas, totalAll };
}

export function formatPayrollMoneyAmount(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

/** Mismos montos que la tabla del cuadro de mando (Total deducciones / Tarjetas). */
export function getCuadroDeductionColumnsForExport(record: {
  totalDeductions?: string | number | null;
  tarjetasDeductions?: string | number | null;
  totalDeductionsExcludingTarjetas?: string | number | null;
  calculatedNewDeductions?: Array<{
    name?: string;
    calculatedAmount?: number;
    amount?: number;
  }> | null;
}): { totalDeductions: string; tarjetas: string } {
  const split = splitPayrollDeductionTotalsForCuadro(record);
  return {
    totalDeductions: formatPayrollMoneyAmount(
      split.totalDeductionsWithoutTarjetas
    ),
    tarjetas: formatPayrollMoneyAmount(split.tarjetas),
  };
}

export function parseDeductionAmount(raw: unknown): number {
  if (typeof raw === "number" && !Number.isNaN(raw)) return raw;
  if (typeof raw === "string") {
    return Number(raw.replace(/[^0-9.-]+/g, "")) || 0;
  }
  return 0;
}

export interface GroupedDeductionRow {
  displayName: string;
  amount: number;
}

/**
 * Agrupa deducciones por nombre normalizado (mismo texto con distinto acento/mayúsculas = una fila).
 */
/**
 * Arma líneas de deducción para el recibo.
 * - Si existe `deductionBreakdown` (detalle del backend), solo se usa eso.
 * - Si no, usa `calculatedNewDeductions` y `calculatedMissingBoxes` como en findPaymentDetail.
 */
export function collectReciboDeductionLines(input: {
  deductionBreakdown?: any[] | null;
  calculatedNewDeductions?: any[] | null;
  calculatedMissingBoxes?: any[] | null;
}): { name: string; amount: number }[] {
  const breakdown = input.deductionBreakdown || [];
  const pushFromBreakdown = () => {
    const lines: { name: string; amount: number }[] = [];
    breakdown.forEach((d: any) => {
      const amt = parseDeductionAmount(d.amount ?? d.calculatedAmount);
      if (amt > 0) {
        lines.push({
          name: String(d.name ?? "Sin nombre").trim() || "Sin nombre",
          amount: amt,
        });
      }
    });
    return lines;
  };

  if (breakdown.length > 0) {
    const fromBreakdown = pushFromBreakdown();
    if (fromBreakdown.length > 0) {
      return fromBreakdown;
    }
    /* breakdown sin montos válidos: no bloquear calculatedNewDeductions */
  }

  const lines: { name: string; amount: number }[] = [];
  (input.calculatedNewDeductions || []).forEach((d: any) => {
    const amt = parseDeductionAmount(d.calculatedAmount ?? d.amount);
    if (amt > 0) {
      lines.push({
        name: String(d.name ?? "Sin nombre").trim() || "Sin nombre",
        amount: amt,
      });
    }
  });
  (input.calculatedMissingBoxes || []).forEach((d: any) => {
    const amt = parseDeductionAmount(d.calculatedAmount ?? d.amount);
    if (amt > 0) {
      lines.push({
        name: String(d.name ?? "Sin nombre").trim() || "Sin nombre",
        amount: amt,
      });
    }
  });
  return lines;
}

export function groupDeductionsForRecibo(
  items: { name: string; amount: number }[]
): GroupedDeductionRow[] {
  const map = new Map<string, { displayName: string; amount: number }>();
  for (const item of items) {
    const amount =
      typeof item.amount === "number" && !Number.isNaN(item.amount)
        ? item.amount
        : 0;
    if (amount <= 0) continue;
    const trimmed = (item.name || "").trim();
    const label = trimmed || "Sin nombre";
    const key = normalizeDeductionGroupKey(label);
    const prev = map.get(key);
    if (!prev) {
      map.set(key, { displayName: label, amount });
    } else {
      prev.amount += amount;
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    a.displayName.localeCompare(b.displayName, "es", { sensitivity: "base" })
  );
}
